import { ChatThread } from '@/components/storyintake/ChatThread'
import { IntakeComposer } from '@/components/storyintake/IntakeComposer'
import { IntakeDoneCard } from '@/components/storyintake/IntakeDoneCard'
import { IntakeIntroCard } from '@/components/storyintake/IntakeIntroCard'
import { IntakeProgressStrip } from '@/components/storyintake/IntakeProgressStrip'
import { StoryDraftReview } from '@/components/storyintake/StoryDraftReview'
import { useStoryBank } from '@/hooks/useStoryBank'
import { useStoryMiner } from '@/hooks/useStoryMiner'
import type { IntakeTrigger } from '@/types/interview'
import { useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

const TRIGGERS: IntakeTrigger[] = ['story_bank', 'onboarding', 'dashboard']

export default function StoryIntakePage() {
  const [searchParams] = useSearchParams()
  const from = searchParams.get('from')
  const trigger: IntakeTrigger =
    TRIGGERS.find((t) => t === from) ?? ('direct' satisfies IntakeTrigger)

  const { storyCount, cap, fetchStories, createStory } = useStoryBank()
  useEffect(() => {
    void fetchStories()
  }, [fetchStories])

  const miner = useStoryMiner({ trigger, createStory, storyCount })
  const { phase } = miner

  const turnsExhausted = miner.turnsRemaining === 0

  return (
    <div className="mx-auto mt-4 max-w-2xl space-y-5 pb-24">
      <Link
        to="/story-bank"
        className="text-xs text-muted-foreground transition-colors hover:text-foreground"
      >
        ← Story Bank
      </Link>

      {(phase === 'intro' || phase === 'starting') && (
        <IntakeIntroCard starting={phase === 'starting'} onStart={(r) => void miner.start(r)} />
      )}

      {phase === 'error' && miner.failure && (
        <section
          role="alert"
          className="space-y-3 rounded-xl border border-border bg-background p-5"
        >
          <p className="text-sm font-semibold">
            {miner.failure.code === 'MINER_SESSION_LIMIT_REACHED'
              ? "You've used this week's conversations"
              : "We couldn't start the conversation"}
          </p>
          <p className="text-sm text-muted-foreground">{miner.failure.message}</p>
          <div className="flex flex-wrap gap-2">
            {miner.failure.code !== 'MINER_SESSION_LIMIT_REACHED' && (
              <button
                type="button"
                onClick={miner.reset}
                className="rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90"
              >
                Try again
              </button>
            )}
            <Link
              to="/story-bank"
              className="rounded-lg border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted"
            >
              Back to my Story Bank
            </Link>
          </div>
        </section>
      )}

      {(phase === 'chatting' || phase === 'drafting') && (
        <>
          <IntakeProgressStrip
            savedCount={miner.savedCount}
            turnsRemaining={miner.turnsRemaining}
            storyCount={storyCount}
            cap={cap}
          />

          {miner.savedCount > 0 && miner.messages.length > 0 && (
            <p className="rounded-lg bg-emerald-500/10 px-3 py-2 text-xs text-emerald-700 dark:text-emerald-400">
              Saved to your Story Bank. Keep going — tell me about another experience, or finish up.
            </p>
          )}

          <ChatThread messages={miner.messages} pending={miner.sending} />

          {miner.draftNotice && (
            <output className="block rounded-lg bg-amber-500/10 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
              {miner.draftNotice === 'non_english'
                ? 'We can only build stories from English right now.'
                : "There isn't quite enough here to draft a story yet — tell me a bit more about what you did."}
            </output>
          )}

          {miner.failure && (
            <p
              role="alert"
              className="rounded-lg bg-destructive/10 px-3 py-2 text-xs text-destructive"
            >
              {miner.failure.message}
            </p>
          )}

          {phase === 'drafting' ? (
            <p className="py-4 text-center text-sm text-muted-foreground">
              <span className="animate-pulse">Writing up your story…</span>
            </p>
          ) : (
            <div className="space-y-3">
              {turnsExhausted && (
                <p className="text-xs text-muted-foreground">
                  That's the end of this conversation's questions
                  {miner.canDraft ? " — turn what you've shared into a story below." : '.'}
                </p>
              )}
              <IntakeComposer
                onSend={miner.send}
                disabled={turnsExhausted}
                sending={miner.sending}
                placeholder={
                  miner.savedCount > 0 ? 'Another experience? Describe it…' : 'Type your answer…'
                }
              />
              <div className="flex flex-wrap items-center justify-between gap-2">
                <button
                  type="button"
                  onClick={() => void miner.requestDraft()}
                  disabled={!miner.canDraft || miner.sending}
                  className={
                    miner.vignetteReady
                      ? 'rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-40'
                      : 'rounded-lg border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted disabled:opacity-40'
                  }
                >
                  {miner.vignetteReady ? 'Turn this into a story →' : 'Turn this into a story'}
                </button>
                <button
                  type="button"
                  onClick={() => void miner.finish()}
                  disabled={miner.sending}
                  className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground disabled:opacity-40"
                >
                  I'm done
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {phase === 'reviewing' && miner.draft && (
        <StoryDraftReview
          draft={miner.draft}
          saving={miner.saving}
          capHit={miner.capHit}
          onSave={(edited) => void miner.saveDraft(edited)}
          onKeepTalking={miner.keepTalking}
        />
      )}

      {phase === 'done' && <IntakeDoneCard savedCount={miner.savedCount} />}
    </div>
  )
}
